import { useEffect, useState } from 'react';
import { listingsApi, type MarketplaceListing, type MarketplaceCode } from '../../api/listings';

const MARKETPLACES: MarketplaceCode[] = ['amazon_in', 'flipkart', 'myntra', 'ajio', 'shopify', 'woocommerce'];

export function ListingManagerPage() {
  const [listings, setListings] = useState<MarketplaceListing[]>([]);
  const [productId, setProductId] = useState('');
  const [marketplace, setMarketplace] = useState<MarketplaceCode>('amazon_in');
  const [title, setTitle] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const reload = () => void listingsApi.list().then(setListings).catch((e) => setError(String(e)));
  useEffect(() => { reload(); }, []);

  const onCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      await listingsApi.create({ product_id: productId, marketplace, content: { title } });
      setMessage('Listing created.');
      setProductId('');
      setTitle('');
      reload();
    } catch (err) {
      setError(String(err));
    }
  };

  const onPublish = async (id: string) => {
    try {
      await listingsApi.approve(id);
      await listingsApi.publish(id);
      reload();
    } catch (err) {
      setError(String(err));
    }
  };

  return (
    <div className="mx-auto max-w-6xl p-6">
      <h1 className="text-2xl font-semibold">Listing Manager</h1>
      <form onSubmit={onCreate} className="mt-6 grid gap-3 rounded-xl border bg-white p-4 sm:grid-cols-2">
        <input className="rounded border px-3 py-2 text-sm" placeholder="Product ID" value={productId} onChange={(e) => setProductId(e.target.value)} required />
        <select className="rounded border px-3 py-2 text-sm" value={marketplace} onChange={(e) => setMarketplace(e.target.value as MarketplaceCode)}>
          {MARKETPLACES.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <input className="rounded border px-3 py-2 text-sm sm:col-span-2" placeholder="Title" value={title} onChange={(e) => setTitle(e.target.value)} required />
        <button type="submit" className="rounded bg-slate-900 px-4 py-2 text-sm text-white sm:col-span-2">Create listing</button>
      </form>
      {message && <p className="mt-4 text-sm text-green-700">{message}</p>}
      {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
      <table className="mt-6 w-full text-left text-sm">
        <thead><tr className="border-b"><th className="py-2">Product</th><th>Marketplace</th><th>Title</th><th>Status</th><th>Health</th><th /></tr></thead>
        <tbody>
          {listings.map((l) => (
            <tr key={l.id} className="border-b">
              <td className="py-2 font-mono text-xs">{l.product_id}</td>
              <td>{l.marketplace}</td>
              <td>{l.content.title}</td>
              <td>{l.status}</td>
              <td>{l.health?.score?.toFixed(0) ?? '—'}</td>
              <td><button type="button" className="text-blue-600" onClick={() => void onPublish(l.id)}>Publish</button></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function ListingEditorPage() {
  const [listingId, setListingId] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const onLoad = async () => {
    if (!listingId) return;
    setError('');
    try {
      const l = await listingsApi.get(listingId);
      setTitle(l.content.title);
      setDescription(l.content.description ?? '');
    } catch (err) {
      setError(String(err));
    }
  };

  const onSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      await listingsApi.update(listingId, { content: { title, description } });
      setMessage('Listing updated.');
    } catch (err) {
      setError(String(err));
    }
  };

  return (
    <div className="mx-auto max-w-6xl p-6">
      <h1 className="text-2xl font-semibold">Listing Editor</h1>
      <div className="mt-6 flex gap-3">
        <input className="rounded border px-3 py-2 text-sm" placeholder="Listing ID" value={listingId} onChange={(e) => setListingId(e.target.value)} />
        <button type="button" className="rounded border px-4 py-2 text-sm" onClick={() => void onLoad()}>Load</button>
      </div>
      <form onSubmit={onSave} className="mt-6 space-y-3 rounded-xl border bg-white p-4">
        <input className="w-full rounded border px-3 py-2 text-sm" placeholder="Title" value={title} onChange={(e) => setTitle(e.target.value)} />
        <textarea className="w-full rounded border px-3 py-2 text-sm" rows={6} placeholder="Description" value={description} onChange={(e) => setDescription(e.target.value)} />
        <button type="submit" className="rounded bg-slate-900 px-4 py-2 text-sm text-white">Save</button>
      </form>
      {message && <p className="mt-4 text-sm text-green-700">{message}</p>}
      {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
    </div>
  );
}

export function MarketplacePreviewPage() {
  const [listingId, setListingId] = useState('');
  const [listing, setListing] = useState<MarketplaceListing | null>(null);

  const onPreview = async () => {
    if (!listingId) return;
    setListing(await listingsApi.get(listingId));
  };

  return (
    <div className="mx-auto max-w-6xl p-6">
      <h1 className="text-2xl font-semibold">Marketplace Preview</h1>
      <div className="mt-6 flex gap-3">
        <input className="rounded border px-3 py-2 text-sm" placeholder="Listing ID" value={listingId} onChange={(e) => setListingId(e.target.value)} />
        <button type="button" className="rounded border px-4 py-2 text-sm" onClick={() => void onPreview()}>Preview</button>
      </div>
      {listing && (
        <div className="mt-6 rounded-xl border bg-white p-6">
          <p className="text-xs uppercase text-slate-400">{listing.marketplace}</p>
          <h2 className="mt-2 text-xl font-semibold">{listing.content.title}</h2>
          {listing.content.subtitle && <p className="text-slate-600">{listing.content.subtitle}</p>}
          <p className="mt-4 text-sm">{listing.content.description}</p>
          {listing.content.bullets && (
            <ul className="mt-4 list-disc pl-5 text-sm">
              {listing.content.bullets.map((b) => <li key={b}>{b}</li>)}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
